import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import violationDetectionService from '../../../services/violationDetectionService';

const RealtimeViolationFeed = ({ violations, loading, onRefresh }) => {
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [remediating, setRemediating] = useState(null);
  const [recentlyAdded, setRecentlyAdded] = useState(new Set());

  // Track recently added violations for visual feedback
  useEffect(() => {
    if (violations?.length > 0) {
      const latestId = violations?.[0]?.id;
      if (latestId && !recentlyAdded?.has(latestId)) {
        setRecentlyAdded(prev => new Set([...prev, latestId]));
        setTimeout(() => {
          setRecentlyAdded(prev => {
            const newSet = new Set(prev);
            newSet?.delete(latestId);
            return newSet;
          });
        }, 3000);
      }
    }
  }, [violations]);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-error',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-success'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  const getSeverityBg = (severity) => {
    const backgrounds = {
      critical: 'bg-error/10 border-error/30',
      high: 'bg-warning/10 border-warning/30',
      medium: 'bg-primary/10 border-primary/30',
      low: 'bg-success/10 border-success/30'
    };
    return backgrounds?.[severity] || 'bg-muted border-border';
  };

  const getRemediationType = (violationType) => {
    const types = {
      minimum_wage_violation: 'minimum_wage_adjustment',
      overtime_rate_violation: 'overtime_recalculation',
      leave_policy_violation: 'leave_balance_correction'
    };
    return types?.[violationType] || null;
  };

  const handleOneClickRemediation = async (violation) => {
    const remediationType = getRemediationType(violation?.violationType);
    if (!remediationType) {
      alert('Automated remediation not available for this violation type');
      return;
    }

    try {
      setRemediating(violation?.id);
      await violationDetectionService?.executeOneClickRemediation(violation?.id, remediationType);
      await onRefresh();
      alert('Remediation completed successfully!');
    } catch (error) {
      console.error('Remediation failed:', error);
      alert('Remediation failed: ' + error?.message);
    } finally {
      setRemediating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Loading violation feed...</p>
      </div>
    );
  }

  if (!violations || violations?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="CheckCircle2" size={64} color="var(--color-success)" className="mx-auto mb-4" />
        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">No Violations Detected</h3>
        <p className="text-sm text-muted-foreground mb-4">All employment practices are compliant with PNG Employment Act</p>
        <Button variant="outline" size="sm" onClick={onRefresh} iconName="RefreshCw">
          Refresh Feed
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-heading font-semibold text-base text-foreground">
          Violation Feed ({violations?.length})
        </h4>
        <Button variant="ghost" size="sm" onClick={onRefresh} iconName="RefreshCw">
          Refresh
        </Button>
      </div>
      <AnimatePresence>
        {violations?.map((violation, index) => (
          <motion.div
            key={violation?.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className={`border rounded-lg p-4 ${getSeverityBg(violation?.severity)} ${
              recentlyAdded?.has(violation?.id) ? 'ring-2 ring-primary animate-pulse' : ''
            }`}
          >
            {/* Violation Header */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Icon 
                    name={violation?.severity === 'critical' ? 'AlertTriangle' : 'AlertCircle'} 
                    size={18} 
                    color={`var(--color-${violation?.severity === 'critical' ? 'error' : violation?.severity === 'high' ? 'warning' : 'primary'})`}
                  />
                  <h5 className="font-heading font-semibold text-sm text-foreground">
                    {violation?.violationType?.replace(/_/g, ' ')?.toUpperCase()}
                  </h5>
                </div>
                <p className="text-xs text-muted-foreground">{violation?.description}</p>
              </div>
              <span className={`px-3 py-1 text-xs font-bold rounded-full whitespace-nowrap ${getSeverityColor(violation?.severity)} bg-card border border-current`}>
                {violation?.severity?.toUpperCase()}
              </span>
            </div>

            {/* Violation Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Affected</p>
                <p className="text-sm font-bold text-foreground">{violation?.affectedCount} employees</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Financial Impact</p>
                <p className="text-sm font-bold text-warning">K{violation?.estimatedCost?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Department</p>
                <p className="text-sm font-medium text-foreground">{violation?.department || 'All'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <p className="text-sm font-medium text-foreground capitalize">
                  {violation?.violationStatus?.replace(/_/g, ' ')}
                </p>
              </div>
            </div>

            {/* Regulation Reference */}
            {violation?.regulationReference && (
              <div className="mb-3 p-2 bg-card/50 rounded">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Regulation:</span> {violation?.regulationReference}
                </p>
              </div>
            )}

            {/* One-Click Remediation Actions */}
            <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border/50">
              {violation?.violationStatus === 'identified' && getRemediationType(violation?.violationType) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleOneClickRemediation(violation)}
                  disabled={remediating === violation?.id}
                  iconName={remediating === violation?.id ? 'Loader2' : 'Zap'}
                >
                  {remediating === violation?.id ? 'Processing...' : 'One-Click Fix'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedViolation(violation?.id)}
                iconName="FileText"
              >
                View Details
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Send"
              >
                Notify Clients
              </Button>
            </div>

            {/* Timestamp */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
              <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
              <p className="text-xs text-muted-foreground">
                Detected {new Date(violation?.identifiedAt)?.toLocaleString()}
              </p>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default RealtimeViolationFeed;